import { fileViewed } from 'features/content-library/actions';
import { GridPanelContainer } from 'features/layout/grid-panel/GridPanelContainer';
import { PanelContentSelector } from 'features/layout/grid-panel/PanelContentSelector';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  fileClosed,
  selectContentLibraryActiveFiles,
  selectContentLibraryOpenedFile,
  selectPreviewedFile,
} from 'features/content-library/contentLibrarySlice';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import EmbedFile from 'features/content-library/components/content-types/EmbedFile';
import { useBulkCloseScopeControl } from 'features/layout/grid-panel/useBulkCloseScopeControl';
import { ContentLibraryOpenedFile } from 'features/content-library/types';
import {
  selectCanSeeContentBadges,
  selectGridPanelReady,
  selectShouldShowContentSelector,
} from 'features/layout/features/content/contentSlice';
import { useContentWhiteboard } from 'features/content-whiteboard/useContentWhiteboard';
import GalleryFile from 'features/content-library/components/content-types/GalleryFile/GalleryFile';
import SinglePageFile from 'features/content-library/components/content-types/SinglePageFile';
import { CONTENT_BOARD_SUPPORTED_FILE_TYPES } from 'features/content-whiteboard/constants';
import { isMultiPageFile } from 'features/content-library/utils/isMultiPageFile';
import { ZoomableContext } from 'features/content-zoom/components/ZoomableContext';
import { ZoomableContent } from 'features/content-zoom/components/ZoomableContent';
import { selectIsContentZoomable } from 'features/content-zoom/contentZoomSlice';
import { isContentLibraryMediaPlayer } from 'features/content-library/utils/isContentLibraryMediaPlayer';
import { buildZoomId } from 'features/content-zoom/utils/formatUtils';
import { useFilePanelCloseControl } from './ContentCloseControl';

const ContentWhiteboardImageViewer = lazy(
  () => import('features/content-whiteboard/components/ContentWhiteboardImageViewer')
);
const ContentWhiteboardGalleryViewer = lazy(
  () => import('features/content-whiteboard/components/ContentWhiteboardGalleryViewer')
);
const MediaPlayerContainer = lazy(
  () => import('features/content-library/components/content-types/MediaPlayer/MediaPlayerContainer')
);
const PreviewVideoPlayer = lazy(
  () => import('features/content-library/components/content-types/MediaPlayer/PreviewVideoPlayer')
);

type ContentPanelProps = {
  size: { width?: number; height?: number };
  isPreview?: boolean;
};

export const ContentPanel = ({ size, isPreview = false }: ContentPanelProps) => {
  const { t } = useTranslation('room');

  const { hasPermissions } = usePermissions();

  const dispatch = useAppDispatch();
  const closeFile = useFilePanelCloseControl();

  const {
    enabled: bulkCloseEnabled,
    label: bulkCloseLabel,
    handleClick: bulkClose,
  } = useBulkCloseScopeControl('files');

  const panelReady = useAppSelector((state) => selectGridPanelReady(state, 'contentLibrary'));

  const files = useAppSelector(selectContentLibraryActiveFiles);

  const liveOpenedFile = useAppSelector(selectContentLibraryOpenedFile);
  const previewedFile = useAppSelector(selectPreviewedFile);
  const openedFile = isPreview ? previewedFile : liveOpenedFile;

  const shouldShowSelector = useAppSelector(selectShouldShowContentSelector);
  const canSeeContentBadges = useAppSelector(selectCanSeeContentBadges);

  const { isFeatureAvailable } = useContentWhiteboard();

  const isContentZoomable = useAppSelector(selectIsContentZoomable);
  const isZoomable = isContentZoomable && !isPreview;

  const canPresentFiles = hasPermissions(PermissionTypes.presentContentLibrary);

  const extraItems = useMemo(() => {
    if (bulkCloseEnabled) {
      return [{ title: bulkCloseLabel, action: bulkClose }];
    }

    return [];
  }, [bulkCloseEnabled, bulkCloseLabel, bulkClose]);

  const removeFile = useCallback(
    (file: ContentLibraryOpenedFile) => {
      dispatch(fileClosed(file.id));
    },
    [dispatch]
  );

  const selectFile = useCallback(
    (idx: number) => {
      dispatch(fileViewed({ id: files[idx].id }));
    },
    [dispatch, files]
  );

  if ((!isPreview && !panelReady) || !openedFile) {
    return null;
  }

  const renderPanelContent = () => {
    if (canPresentFiles && isMultiPageFile(openedFile)) {
      if (isFeatureAvailable) {
        return (
          <Suspense fallback={null}>
            <ContentWhiteboardGalleryViewer file={openedFile} size={size} isPreview={isPreview} />
          </Suspense>
        );
      }

      return <GalleryFile file={openedFile} size={size} isPreview={isPreview} />;
    }

    if (openedFile.type === 'webapp') {
      return <EmbedFile file={openedFile} />;
    }

    if (isContentLibraryMediaPlayer(openedFile)) {
      return (
        <Suspense fallback={null}>
          {isPreview ? (
            <PreviewVideoPlayer file={openedFile} />
          ) : (
            <MediaPlayerContainer file={openedFile} />
          )}
        </Suspense>
      );
    }

    if (isFeatureAvailable && CONTENT_BOARD_SUPPORTED_FILE_TYPES.includes(openedFile.type)) {
      return (
        <Suspense fallback={null}>
          <ContentWhiteboardImageViewer file={openedFile} />
        </Suspense>
      );
    }

    return <SinglePageFile file={openedFile} />;
  };

  const content = renderPanelContent();

  return (
    <GridPanelContainer id="content-container">
      {canSeeContentBadges && (
        <PanelContentSelector
          items={files}
          activeItem={openedFile}
          onSelect={selectFile}
          onRemoveItem={closeFile.controlEnabled ? removeFile : undefined}
          extraItems={extraItems}
          renderLabel={(file) => file?.name ?? ''}
          getItemId={(file) => file?.id}
          disabled={!shouldShowSelector}
        />
      )}
      {isZoomable ? (
        <ZoomableContext>
          <ZoomableContent
            zoomId={buildZoomId(openedFile.id, openedFile.page)}
            ariaLabel={t('tooltips.content_zoom_aria_label')}
          >
            {content}
          </ZoomableContent>
        </ZoomableContext>
      ) : (
        content
      )}
    </GridPanelContainer>
  );
};
